import React, { FC, useContext, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import useIsMobileDimension from "../../hooks/useIsMobileDimension";
import {
  HEAP_EVENTS,
  SignateraRenewalInterestSource,
} from "@app/provider/types";
import {
  NotificationContext,
  OrderRenewalContext,
  UserContext,
} from "@app/provider";
import Dialog from "@natera/material/lib/dialog";
import Button from "@natera/material/lib/button";
import ResponceImage from "@assets/images/renewalPatientResponce.png";

import "./renewalPopUp.scss";

const messages = defineMessages({
  renewalPopUpTitle: {
    id: "renewalPopUpTitle",
    defaultMessage: "Are you interested in continuing to test with Signatera?",
  },
  renewalPopUpSubtitle: {
    id: "renewalPopUpSubtitle",
    defaultMessage:
      "If yes, Natera will work with your healthcare team to ensure no gaps in testing.",
  },
  renewalPopUpAgreeButton: {
    id: "renewalPopUpAgreeButton",
    defaultMessage: "Yes, I’m interested in continued testing",
  },
  renewalPopUpDisagreeButton: {
    id: "renewalPopUpDisagreeButton",
    defaultMessage: "No, I am not interested in further testing now",
  },
  renewalPopUpGratitude: {
    id: "renewalPopUpGratitude",
    defaultMessage: "Thanks for your response",
  },
});
const IS_RENEWAL = true;
const ANIMATION_DURATION = 600;

interface RenewalPopUpProps {
  onCloseDialog: () => void;
  limsId?: number;
  orderUidPublic?: string;
}

const RenewalPopUp: FC<RenewalPopUpProps> = ({
  onCloseDialog,
  limsId,
  orderUidPublic,
}) => {
  const intl = useIntl();
  const isMobile = useIsMobileDimension();
  const { uppUser } = useContext(UserContext);
  const { clear } = useContext(NotificationContext);
  const { handleSetPatientInterested } = useContext(OrderRenewalContext);

  const [activeDialog, setActiveDialog] = useState<"first" | "second" | null>(
    "first"
  );
  const [closingDialog, setClosingDialog] = useState<"first" | "second" | null>(
    null
  );

  React.useEffect(() => {
    if (isMobile && activeDialog) {
      const element = document.querySelector(`.popUp-dialog.mobile`);
      if (element) {
        element.classList.add("animate-up");
      }
    }
  }, [isMobile, activeDialog]);

  const handleCloseFirstDialog = () => {
    setClosingDialog("first");
    setTimeout(() => {
      setActiveDialog("second");
      setClosingDialog(null);
    }, ANIMATION_DURATION);
  };

  const handleCloseSecondDialog = () => {
    setClosingDialog("second");
    setTimeout(() => {
      clear();
      onCloseDialog();
    }, ANIMATION_DURATION);
  };

  const handleAgree = async () => {
    heap.track(HEAP_EVENTS.upp_renewal_signatera_yes_interested, {
      identity: uppUser?.patientUID,
      lims_clinic_id: limsId,
      public_order_uid: orderUidPublic,
      source: SignateraRenewalInterestSource.UPP,
    });
    try {
      await handleSetPatientInterested(true, IS_RENEWAL);
    } catch (error) {
      console.error("Error setting patient interest:", error);
    } finally {
      handleCloseFirstDialog();
    }
  };

  const handleDisagree = async () => {
    heap.track(HEAP_EVENTS.upp_renewal_signatera_not_interested, {
      identity: uppUser?.patientUID,
      lims_clinic_id: limsId,
      public_order_uid: orderUidPublic,
    });
    try {
      await handleSetPatientInterested(false, IS_RENEWAL);
    } catch (error) {
      console.error("Error setting patient interest:", error);
    } finally {
      handleCloseFirstDialog();
    }
  };

  return (
    <>
      {activeDialog === "first" && (
        <Dialog
          title={""}
          showCrossButton
          onClose={onCloseDialog}
          className={`popUp-dialog ${isMobile ? "mobile" : ""} ${
            closingDialog === "first" ? "animate-down" : ""
          }`}
        >
          <div className="popUp-dialog__content">
            <div className="popUp-dialog__content__header-container">
              <h3 className="popUp-dialog__content__header">
                {intl.formatMessage(messages.renewalPopUpTitle)}
              </h3>
              <p className="popUp-dialog__content__subtitle">
                {intl.formatMessage(messages.renewalPopUpSubtitle)}
              </p>
            </div>
            <Button
              className="popUp-dialog__content__button"
              aria-label={intl.formatMessage(messages.renewalPopUpAgreeButton)}
              onClick={handleAgree}
            >
              {intl.formatMessage(messages.renewalPopUpAgreeButton)}
            </Button>
            <Button
              className="popUp-dialog__content__button"
              aria-label={intl.formatMessage(
                messages.renewalPopUpDisagreeButton
              )}
              onClick={handleDisagree}
            >
              {intl.formatMessage(messages.renewalPopUpDisagreeButton)}
            </Button>
          </div>
        </Dialog>
      )}

      {activeDialog === "second" && (
        <Dialog
          title={""}
          showCrossButton
          onClose={handleCloseSecondDialog}
          className={`popUp-dialog ${isMobile ? "mobile" : ""} ${
            closingDialog === "second" ? "animate-down" : ""
          }`}
        >
          <div className="popUp-dialog__content">
            <img
              src={ResponceImage}
              alt={intl.formatMessage(messages.renewalPopUpGratitude)}
            />
            <h3>{intl.formatMessage(messages.renewalPopUpGratitude)}</h3>
          </div>
        </Dialog>
      )}
    </>
  );
};

export default RenewalPopUp;
